import { GrfqVenue } from '#/unified-rfqs/entities/products/venue';
import { AuctionType } from '#/unified-rfqs/entities/shared';
import { getEffectiveQuantityPrecision, getProduct } from '#/products/domain';
import { DraftLeg } from './types';
import { isHedgeLeg } from './leg-functions';

/**
 * Distinct product codes across non-hedge legs, first-appearance order.
 * Legs without a product are skipped.
 */
export function rfqProductCodes(legs: readonly DraftLeg[]): readonly string[] {
  const codes: string[] = [];
  for (const leg of legs) {
    if (isHedgeLeg(leg)) continue;
    const { productCode } = leg;
    if (productCode == null) continue;
    if (!codes.includes(productCode)) codes.push(productCode);
  }
  return codes;
}

export function spansMultipleProducts(legs: readonly DraftLeg[]): boolean {
  return rfqProductCodes(legs).length > 1;
}

export const HEDGE_ON_MULTI_PRODUCT_MESSAGE =
  'Hedge legs are not supported on multi-product strategies';

/**
 * Whether the builder may hold legs on more than one product:
 * the flag's venue allow-list must include the selected venue, and only the
 * RFQ auction opts in — the order book (OB / GRFQ) keeps the strict
 * single-product rule permanently, matching the backend's serializer hook.
 */
export function isMultiProductAllowed(
  multiProductVenues: readonly string[],
  venue: GrfqVenue['code'],
  auction: AuctionType | null,
): boolean {
  return auction === 'RFQ' && multiProductVenues.includes(venue);
}

/**
 * Structure-level rule check for a (potentially) multi-product leg set.
 * Returns a user-facing error message, or undefined when the structure is
 * fine. Mirrors the backend's checks in order; single-product structures
 * always pass.
 */
export function checkMultiProductStructure(
  legs: readonly DraftLeg[],
  allowMultiProduct: boolean,
): string | undefined {
  const codes = rfqProductCodes(legs);
  if (codes.length <= 1) return undefined;

  if (!allowMultiProduct) return 'All legs must be on the same product';

  const products = codes.map((code) => getProduct(code));

  const quoteCurrencies = new Set(products.map((p) => p.quoteCurrency));
  if (quoteCurrencies.size > 1)
    return 'All legs must share the same quote currency';

  const contractSizes = new Set(products.map((p) => p.contractSize));
  if (contractSizes.size > 1)
    return 'All legs must share the same contract multiplier';

  if (legs.some(isHedgeLeg)) return HEDGE_ON_MULTI_PRODUCT_MESSAGE;

  return undefined;
}

/**
 * Coarsest (fewest-decimals) quantity precision across the package's own
 * non-hedge legs, each resolved with its own product AND its own dated-ness
 * (a leg's `isDated` argument to getEffectiveQuantityPrecision — for FUTURE
 * legs this is `leg.expiration != null`; OPTION legs are never "dated" in
 * this sense, matching the existing call sites). `undefined` when the
 * structure is single-product (or has fewer than 2 legs with a resolvable
 * product) — callers must leave the leg's own precision alone in that case.
 * Mirrors the backend's `min_quantity_increment`: coarsest across legs,
 * ratio-blind.
 */
export function coarsestQuantityPrecision(
  legs: readonly DraftLeg[],
): number | undefined {
  if (!spansMultipleProducts(legs)) return undefined;

  let coarsest: number | undefined;
  for (const leg of legs) {
    // Binding to an explicitly-typed intermediate avoids isHedgeLeg's type
    // predicate narrowing `leg` to exclude every FUTURE leg below (it only
    // guards its own true branch to Draft<DraftFutureLeg>, not "FUTURE and
    // not hedge" vs "everything else") — we still need non-hedge FUTURE
    // legs' `kind`/`expiration` after this check. `Boolean(...)` would also
    // suppress the narrowing but trips `no-extra-boolean-cast`.
    const isHedge: boolean = isHedgeLeg(leg);
    if (isHedge) continue;
    const { productCode } = leg;
    if (productCode == null) continue;

    const isDated = leg.kind === 'FUTURE' && leg.expiration != null;
    const precision = getEffectiveQuantityPrecision(
      getProduct(productCode),
      isDated,
    );
    if (coarsest === undefined || precision < coarsest) coarsest = precision;
  }
  return coarsest;
}
